import React, { useState } from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { usePortal } from '../contexts/PortalContext';
import { brand } from '../data/projects';
type Mode = 'signin' | 'forgot';
const FIELD = 'mt-4 w-full border-0 border-b border-paper/25 bg-transparent pb-3 font-display text-xl text-paper placeholder:text-paper/25 focus:border-paper focus:outline-none';
const COPY: Record<Mode, {
  label: string;
  title: string;
}> = {
  signin: {
    label: 'Staff Access',
    title: 'Sign in to\nPortal.'
  },
  forgot: {
    label: 'Password Reset',
    title: 'Reset your\npassword.'
  }
};

/** Studio-side access. Kept entirely separate from client access. */
export function StaffLogin() {
  const {
    staffSignIn,
    staffResetPassword,
    notify
  } = usePortal();
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  function go(next: Mode) {
    setMode(next);
    setPassword('');
    setError('');
  }
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      if (mode === 'signin') {
        const result = await staffSignIn(email, password);
        if (!result.ok) setError(result.error ?? 'Email or password incorrect');
        return;
      }
      if (!email.trim()) {
        setError('Enter your Portal email address.');
        return;
      }
      await staffResetPassword(email);
      notify('Password reset email sent, if that address has Portal access');
      go('signin');
    } finally {
      setBusy(false);
    }
  }
  const submitLabel = mode === 'signin' ? 'Sign In to Portal' : 'Send Reset Email';
  return <div className="flex min-h-screen w-full items-center justify-center bg-ink px-6 py-20 text-paper" data-id="element-840" data-mp="3o39b9an9hk9">
      <div className="w-full max-w-md" data-id="element-841" data-mp="3v2r6j2o023k4">
        <img src={brand.lockup} alt="Portal Productions" className="h-8 w-auto brightness-0 invert sm:h-10" data-id="element-842" data-mp="3v1pqskw1gsw2" />

        <p className="label mt-16 text-paper/50" data-id="element-843" data-mp="hvy7x13g5t57">{COPY[mode].label}</p>

        <h1 className="mt-7 whitespace-pre-line font-display text-4xl leading-[0.95] tracking-tight sm:text-5xl" data-id="element-844" data-mp="mrwzsynzhtad">
          {COPY[mode].title}
        </h1>

        <form onSubmit={submit} className="mt-14" data-id="element-845" data-mp="d2dihxfx67hm">
          <label className="block" data-id="element-846" data-mp="d0slm164ucgl">
            <span className="label block text-paper/40" data-id="element-847" data-mp="3v7ytz7m9ie3o">Email</span>
            <input type="email" value={email} onChange={event => setEmail(event.target.value)} placeholder="e.g. name@portalproductions.me" autoComplete="username" className={FIELD} data-id="element-848" data-mp="3vaky0x9y2bh5" />
          </label>

          {mode === 'signin' && <label className="mt-10 block" data-id="element-849" data-mp="1kfxe5wq76lyy">
              <span className="label block text-paper/40" data-id="element-850" data-mp="1kh8efmkfpblb">Password</span>
              <input type="password" value={password} onChange={event => setPassword(event.target.value)} autoComplete="current-password" className={FIELD} data-id="element-851" data-mp="1oh0s2dooq7xj" />
            </label>}

          {error && <p role="alert" className="label-sm mt-8 leading-relaxed text-paper" data-id="element-852" data-mp="3iqo14scf4e2m">
              {error}
            </p>}

          <button type="submit" disabled={busy} className="label group mt-14 flex w-full items-center justify-between gap-6 border border-paper px-8 py-5 text-paper transition-colors duration-150 ease-out hover:bg-paper hover:text-ink disabled:border-paper/25 disabled:text-paper/40 disabled:hover:bg-transparent disabled:hover:text-paper/40" data-id="element-853" data-mp="3dx33x2info4h">
            {submitLabel}
            <ArrowRightIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:translate-x-1" strokeWidth={1.25} data-id="element-854" data-mp="27aqxrz3u1rlv" />
          </button>
        </form>

        <div className="mt-10 flex flex-wrap items-center gap-8" data-id="element-855" data-mp="24queczhhvg3m">
          {mode === 'signin' ? <button type="button" onClick={() => go('forgot')} className="label-sm text-paper/35 underline underline-offset-4 transition-colors duration-150 ease-out hover:text-paper/70" data-id="element-856" data-mp="28rd50q7nig48">
              Forgot password?
            </button> : <button type="button" onClick={() => go('signin')} className="label-sm text-paper/35 underline underline-offset-4 transition-colors duration-150 ease-out hover:text-paper/70" data-id="element-857" data-mp="1l33h9iqvniag">
              Back to sign in
            </button>}
        </div>

        <p className="label-sm mt-16 max-w-sm leading-relaxed text-paper/25" data-id="element-858" data-mp="1zrztlrzyvhgv">
          Portal team accounts only, by invitation. Client access is provided separately by secure
          project link.
        </p>
      </div>
    </div>;
}